// Shared HTTP transport: typed requests, auth headers, and automatic token
// refresh. Feature endpoint modules (features/*/api.ts) build on top of this
// thin core — it holds no feature-specific endpoints itself.

import type { ApiError, SessionResponse } from '@/types/api';
import { getAccessToken, setAccessToken, setStoredUser, clearTokens } from '@/lib/auth';
import { type ZodType, z } from 'zod';
import { captureException } from '@sentry/react';

export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Error carrying the HTTP status so callers (e.g. React Query retry policy) can
// react to auth failures without brittle string matching on the message. The
// optional machine-readable backend code (e.g. DUPLICATE_ITEM_TITLE) lets
// callers branch on a specific failure without matching on the human message.
export class ApiRequestError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

// Merge the current bearer token into a headers object. Exposed so the few
// callers that must own their own `fetch` (e.g. PIN verify, which needs custom
// 401 handling) still share the same auth-header injection as apiFetch.
export function authHeaders(base: Record<string, string> = {}): Record<string, string> {
  const accessToken = getAccessToken();
  return {
    ...base,
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
}

// ---------------------------------------------------------------------------
// Token refresh. The refresh token is a rotating httpOnly cookie shared by
// every context on this origin (tabs, installed PWA). Uncoordinated
// concurrent POSTs to /auth/refresh race on that single cookie, so refreshes
// are single-flighted twice over:
//   - within this JS context via a shared module-level promise, and
//   - across contexts via a Web Lock (navigator.locks).
// The backend additionally tolerates a lost race with a short reuse grace
// window, but coordinated clients shouldn't need it (#462).
// ---------------------------------------------------------------------------

// Web Lock name shared by every context on this origin.
const REFRESH_LOCK = 'nomi-auth-refresh';

// In-context single-flight: concurrent callers all await the same refresh.
let refreshPromise: Promise<void> | null = null;

// POST /auth/refresh and store the new session. Throws ApiRequestError with
// the HTTP status on failure (0 for network failures) and has no redirect
// side effects — callers decide how fatal a failure is.
async function requestRefresh(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // No body: the httpOnly refresh cookie is sent automatically (same-origin).
    });
  } catch (err) {
    // Network failure (offline / DNS / connection refused). Surface a typed
    // error with status 0 so callers can distinguish "offline" from an HTTP
    // status, and so no auth-logout path is triggered by a mere network drop.
    throw new ApiRequestError(err instanceof Error ? err.message : 'Network error', 0);
  }

  if (!response.ok) {
    throw new ApiRequestError('Token refresh failed', response.status);
  }

  const data: SessionResponse = await response.json();
  setAccessToken(data.accessToken);
  setStoredUser(data.user);
}

// Run fn while holding the cross-context refresh lock, where the Web Locks
// API is available (all evergreen browsers; falls back to an uncoordinated
// call elsewhere — the backend grace window still covers that race).
async function withRefreshLock(fn: () => Promise<void>): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    await navigator.locks.request(REFRESH_LOCK, fn);
    return;
  }
  await fn();
}

// Refresh the session exactly once no matter how many callers need it:
// concurrent callers in this context share one promise, and other tabs / the
// installed PWA are serialized behind the Web Lock. If another context
// already refreshed while we waited for the lock (the localStorage access
// token changed under us), the redundant network call is skipped. Never
// redirects; throws ApiRequestError on failure. Exported for the auth
// bootstrap in useAuth, which must share this guard but render logged-out
// silently on failure instead of redirecting.
export function refreshSession(): Promise<void> {
  if (!refreshPromise) {
    const tokenBeforeLock = getAccessToken();
    refreshPromise = withRefreshLock(async () => {
      const currentToken = getAccessToken();
      if (currentToken && currentToken !== tokenBeforeLock) {
        // Another tab/PWA context refreshed while we waited for the lock —
        // the shared localStorage already holds the fresh session.
        return;
      }
      await requestRefresh();
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Session refresh for callers inside an active session: on a DEFINITIVE auth
// failure (the server rejected the refresh token — it is revoked or expired)
// the local session is dead, so clear it and hard-redirect to /login. Any
// transient failure (offline, 5xx, proxy hiccup) just throws so the caller
// surfaces an error without logging the user out.
export async function refreshAccessToken(): Promise<void> {
  try {
    await refreshSession();
  } catch (err) {
    if (err instanceof ApiRequestError && (err.status === 401 || err.status === 403)) {
      clearTokens();
      window.location.href = '/login';
      throw new Error('Token refresh failed', { cause: err });
    }
    throw err;
  }
}

// Build an ApiRequestError from a failed response, tolerating non-JSON bodies
// (e.g. a proxy 502 or an empty body) instead of throwing while parsing.
async function toApiError(response: Response): Promise<ApiRequestError> {
  const errorData: ApiError = await response.json().catch(() => ({}) as ApiError);
  return new ApiRequestError(errorData.error || 'Request failed', response.status, errorData.code);
}

// Validates a parsed JSON body against a zod schema, reporting drift to Sentry
// before rethrowing so backend/frontend contract mismatches surface as alerts
// instead of silent `undefined` access downstream.
function parseOrCapture<S extends ZodType>(schema: S, json: unknown, path: string): z.infer<S> {
  try {
    return schema.parse(json);
  } catch (err) {
    captureException(err, { tags: { kind: 'contract-drift', path } });
    throw err;
  }
}

// Shared auth + 401-refresh transport. Attaches the bearer token, and on a 401
// refreshes the token once and retries. Returns the raw Response on success so
// callers can consume it as JSON, a Blob, or nothing; throws ApiRequestError on
// failure. This is the single code path that uploads/previews/exports reuse so
// they inherit the same refresh behavior as apiFetch.
async function authorizedFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const url = `${API_BASE}${path}`;
  const accessToken = getAccessToken();

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Add Authorization header if token exists
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
    });
  } catch (err) {
    // Network failure (offline / DNS / connection refused). Surface a typed
    // error with status 0 so callers can distinguish "offline" from an HTTP
    // status, and so no auth-logout path is triggered by a mere network drop.
    throw new ApiRequestError(err instanceof Error ? err.message : 'Network error', 0);
  }

  // Handle 401 with token refresh — but only when we actually hold an access
  // token that may have expired. A 401 with no access token means the caller
  // is logged out (e.g. submitting the login form with bad credentials); there
  // is nothing to refresh, so throw the error instead of firing a doomed
  // cookie-refresh that would redirect to /login and swallow the error.
  if (response.status === 401 && accessToken) {
    // A window-focus refetch burst can 401 many requests at once: they all
    // await the same single-flight refresh (refreshSession) and then each
    // retries itself — no request is dropped just because a refresh was
    // already in flight.
    await refreshAccessToken();

    // Retry original request with new token
    const newAccessToken = getAccessToken();
    if (newAccessToken) {
      headers['Authorization'] = `Bearer ${newAccessToken}`;
    }

    const retryResponse = await fetch(url, {
      ...options,
      headers,
    });

    if (!retryResponse.ok) {
      if (retryResponse.status === 401) {
        // Still 401 after a successful refresh, redirect to login
        clearTokens();
        window.location.href = '/login';
      }
      throw await toApiError(retryResponse);
    }

    return retryResponse;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response;
}

// (1) schema present → parsed, inferred type.
export async function apiFetch<S extends ZodType>(
  path: string,
  options: RequestInit & { schema: S },
): Promise<z.infer<S>>;
// (2) No schema → no body is consumed; caller gets nothing back.
export async function apiFetch(path: string, options?: RequestInit): Promise<void>;
export async function apiFetch(
  path: string,
  options: RequestInit & { schema?: ZodType } = {},
): Promise<unknown> {
  const { schema, ...init } = options;
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };

  // Add Content-Type for POST/PUT/PATCH
  if (init.method && ['POST', 'PUT', 'PATCH'].includes(init.method)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await authorizedFetch(path, { ...init, headers });

  // No schema (void call) or an empty 204: consume nothing. Parsing the body
  // for a void response is wasted work and would throw on an empty/non-JSON
  // body (e.g. a 200 with no content).
  if (!schema || response.status === 204) {
    return undefined;
  }

  const json = await response.json();
  return parseOrCapture(schema, json, path);
}

// Multipart upload sharing apiFetch's auth + 401-refresh path. Content-Type is
// intentionally left unset so the browser adds the multipart boundary.
export async function apiUpload<S extends ZodType>(
  path: string, field: string, file: File, options: { schema: S },
): Promise<z.infer<S>>;
export async function apiUpload(
  path: string, field: string, file: File, options: { schema?: ZodType } = {},
): Promise<unknown> {
  const formData = new FormData();
  formData.append(field, file);
  const response = await authorizedFetch(path, { method: 'POST', body: formData });
  const json = await response.json();
  if (!options.schema) return json;
  return parseOrCapture(options.schema, json, path);
}

// Binary GET (previews/exports) sharing apiFetch's auth + 401-refresh path.
export async function apiFetchBlob(path: string): Promise<Blob> {
  const response = await authorizedFetch(path);
  return response.blob();
}
